/*
 * Elixir theme behaviour, without a framework:
 * the day-to-night palette and clock, reveals, parallax, the product gallery and quantity,
 * the bag drawer (Shopify's AJAX cart + Section Rendering API) and the 10% popup.
 * Everything here is progressive: the theme still works with JavaScript off.
 */
(() => {
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const root = document.documentElement;

  /* ---------- a day in the life of the page (same stops as src/lib/daylight.ts) ---------- */

  const DAY = { fg: "#1d1712", muted: "#6a5f55", accent: "#1d1712" };
  const NIGHT = { fg: "#f3ebdf", muted: "#c2b3a3", accent: "#ee974f" };
  const stops = [
    { at: 0.0, p: { bg: "#edebe0", card: "#f6f4ec", card2: "#e3e1d3" } }, // morning ivory
    { at: 0.25, p: { bg: "#ece6d8", card: "#f4efe4", card2: "#e2dac8" } },
    { at: 0.45, p: { bg: "#eadcc4", card: "#f2e7d4", card2: "#dfceb2" } }, // golden hour
    { at: 0.58, p: { bg: "#dfc5b4", card: "#eadacd", card2: "#d4b6a4" } }, // rose dusk
    { at: 0.66, p: { bg: "#633f27", card: "#6e4830", card2: "#77503a" } }, // amber glass
    { at: 0.8, p: { bg: "#2f2219", card: "#3a2a1e", card2: "#43311f" } }, // candlelight
    { at: 1.0, p: { bg: "#241912", card: "#2f2219", card2: "#3a2a1e" } }, // ember, late
  ];
  const NIGHT_FROM = 0.62;

  const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const mix = (a, b, t) => hex(a).map((v, i) => Math.round(v + (hex(b)[i] - v) * t));
  const luminance = (rgb) => {
    const [r, g, b] = rgb.map((v) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const rgb = (c) => `rgb(${c.join(" ")})`;

  function paletteAt(progress) {
    const x = Math.min(1, Math.max(0, progress));
    let i = 0;
    while (i < stops.length - 2 && x > stops[i + 1].at) i++;
    const a = stops[i];
    const b = stops[i + 1];
    const t = (x - a.at) / (b.at - a.at || 1);
    const bg = mix(a.p.bg, b.p.bg, t);
    const lum = luminance(bg);
    // text follows the background's actual brightness, so it never sits mid-tone on mid-tone
    const ink = lum > 0.3 ? DAY : NIGHT;
    return {
      bg: rgb(bg),
      card: rgb(mix(a.p.card, b.p.card, t)),
      card2: rgb(mix(a.p.card2, b.p.card2, t)),
      fg: ink.fg,
      muted: ink === DAY ? (lum > 0.55 ? DAY.muted : "#4f4338") : NIGHT.muted,
      accent: ink.accent,
    };
  }

  /** 07.00 at the top of the page → 23.30 at the bottom, in 15-minute steps. */
  function timeAt(progress) {
    const mins = 7 * 60 + Math.round((Math.min(1, Math.max(0, progress)) * (16.5 * 60)) / 15) * 15;
    return `${String(Math.floor(mins / 60)).padStart(2, "0")}.${String(mins % 60).padStart(2, "0")}`;
  }

  const scrollProgress = () => {
    const max = root.scrollHeight - innerHeight;
    return max > 0 ? scrollY / max : 0;
  };

  const clockTime = document.querySelector("[data-clock-time]");
  const clockFill = document.querySelector("[data-clock-fill]");
  const clockSun = document.querySelector("[data-clock-sun]");
  const logo = document.querySelector("[data-logo]");
  const parallax = [...document.querySelectorAll("[data-parallax]")];
  const lift = document.querySelector("[data-lift]");

  let frame = 0;
  function paint() {
    frame = 0;
    const p = scrollProgress();
    const c = paletteAt(p);
    root.style.setProperty("--bg", c.bg);
    root.style.setProperty("--fg", c.fg);
    root.style.setProperty("--muted", c.muted);
    root.style.setProperty("--card", c.card);
    root.style.setProperty("--card2", c.card2);
    root.style.setProperty("--accent", c.accent);

    const night = p > NIGHT_FROM;
    if (clockTime) clockTime.textContent = timeAt(p);
    if (clockFill) clockFill.style.width = `${p * 100}%`;
    if (clockSun) {
      clockSun.style.left = `${p * 100}%`;
      clockSun.style.background = night ? "transparent" : "currentColor";
      clockSun.style.boxShadow = night ? "inset -3px -1px 0 0 currentColor" : "none";
    }
    if (logo) logo.style.color = night ? "var(--color-gold)" : "var(--color-brand)";

    if (reduce) return;
    // photographs drift gently against the scroll, so they feel deep rather than flat
    for (const el of parallax) {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > innerHeight) continue;
      const amount = Number(el.dataset.parallax) || 6;
      const t = (innerHeight - r.top) / (innerHeight + r.height); // 0 → 1 across the viewport
      const inner = el.firstElementChild;
      if (inner) inner.style.transform = `translateY(${(t * 2 - 1) * amount}%)`;
    }
    if (lift) lift.style.transform = `translateY(${Math.max(-60, (-scrollY / 900) * 60)}px)`;
  }
  const onScroll = () => {
    if (!frame) frame = requestAnimationFrame(paint);
  };
  paint();
  addEventListener("scroll", onScroll, { passive: true });
  addEventListener("resize", onScroll);

  /* ---------- reveals: things fade up as they enter the viewport ---------- */

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add("is-in");
        io.unobserve(e.target);
      }
    },
    { rootMargin: "0px 0px -60px 0px" },
  );
  function watch(scope) {
    scope.querySelectorAll("[data-reveal], [data-words]:not([data-immediate]), [data-nerve]").forEach((el) => io.observe(el));
    // lines that play on load (the hero, a product's name)
    requestAnimationFrame(() => scope.querySelectorAll("[data-words][data-immediate]").forEach((el) => el.classList.add("is-in")));
  }
  watch(document);
  // the theme editor re-renders sections in place: watch the new markup too
  document.addEventListener("shopify:section:load", (e) => watch(e.target));

  /* ---------- product gallery ---------- */

  document.querySelectorAll("[data-gallery]").forEach((g) => {
    const slides = [...g.querySelectorAll("[data-slide]")];
    const thumbs = [...g.querySelectorAll("[data-thumb]")];
    // swipe the main photo left or right on a phone to move through the gallery
    const main = g.querySelector("[data-gallery-main]");
    if (main && thumbs.length > 1) {
      let x0 = null;
      main.addEventListener("touchstart", (e) => (x0 = e.touches[0].clientX), { passive: true });
      main.addEventListener(
        "touchend",
        (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          x0 = null;
          if (Math.abs(dx) < 40) return;
          const at = thumbs.findIndex((t) => t.classList.contains("is-active"));
          const next = (at + (dx < 0 ? 1 : -1) + thumbs.length) % thumbs.length;
          thumbs[next].click();
          thumbs[next].scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
        },
        { passive: true },
      );
    }
    thumbs.forEach((btn) =>
      btn.addEventListener("click", () => {
        const n = btn.dataset.thumb;
        for (const s of slides) {
          const on = s.dataset.slide === n;
          if (on) s.hidden = false;
          requestAnimationFrame(() => (s.style.opacity = on ? "1" : "0"));
          if (!on) setTimeout(() => s.style.opacity === "0" && (s.hidden = true), 700);
        }
        for (const t of thumbs) {
          const on = t === btn;
          t.setAttribute("aria-current", String(on));
          t.classList.toggle("is-active", on);
        }
        // films play only while they're showing
        for (const sl of slides) {
          const v = sl.querySelector("video");
          if (!v) continue;
          if (sl.dataset.slide === n) v.play().catch(() => {});
          else v.pause();
        }
      }),
    );
  });

  /* ---------- quantity and line price on the product page ---------- */

  const money = (cents) => {
    const pounds = cents / 100;
    return `£${Number.isInteger(pounds) ? pounds : pounds.toFixed(2)}`;
  };
  const initForm = (form) => {
    const qty = form.querySelector("[data-qty]");
    const select = form.querySelector("[data-variant-select]");
    const plans = [...form.querySelectorAll('input[name="selling_plan"]')];
    // the line price shows in the form and on the sticky bar, which sits outside it
    const outs = [...form.querySelectorAll("[data-line-price]"), ...document.querySelectorAll(`[data-sticky-bar] [data-line-price]`)];
    const planShort = document.querySelector("[data-sticky-bar] [data-plan-short]");
    const unit = () => {
      const plan = plans.find((p) => p.checked);
      if (plan) return Number(plan.dataset.price);
      return Number(select?.selectedOptions[0]?.dataset.price ?? form.dataset.unitPrice);
    };
    const update = () => {
      const n = Math.min(24, Math.max(1, Number(qty.value) || 1));
      qty.value = n;
      outs.forEach((o) => (o.textContent = money(unit() * n)));
      const plan = plans.find((p) => p.checked);
      if (plan && planShort) planShort.textContent = plan.dataset.planShort;
    };
    plans.forEach((p) => p.addEventListener("change", update));
    form.querySelectorAll("[data-qty-step]").forEach((b) =>
      b.addEventListener("click", () => {
        qty.value = Number(qty.value) + Number(b.dataset.qtyStep);
        update();
      }),
    );
    qty?.addEventListener("change", update);
    select?.addEventListener("change", update);
  };
  document.querySelectorAll("form[data-unit-price]").forEach(initForm);

  // sizes: fetch this section for the chosen variant and swap in its buy box and sticky bar
  document.querySelectorAll("[data-variant-picker]").forEach((picker) => {
    picker.addEventListener("change", async (e) => {
      const id = e.target.value;
      const url = `${picker.dataset.url}?variant=${id}`;
      history.replaceState(null, "", url);
      const res = await fetch(`${url}&section_id=${picker.dataset.section}`);
      if (!res.ok) return location.assign(url);
      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      for (const sel of ["[data-buy-box]", "[data-sticky-bar]"]) {
        const fresh = doc.querySelector(sel);
        const here = document.querySelector(sel);
        if (fresh && here) here.replaceWith(fresh);
      }
      document.querySelectorAll("[data-buy-box] form[data-unit-price]").forEach(initForm);
    });
  });

  /* ---------- the bag: Shopify's AJAX cart, re-rendered through the Section Rendering API ---------- */

  const drawerSectionId = () => document.getElementById("cart-drawer")?.closest(".shopify-section")?.id.replace("shopify-section-", "");
  const routes = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";

  function openDrawer() {
    const d = document.getElementById("cart-drawer");
    if (!d) return false;
    d.classList.add("is-open");
    document.body.style.overflow = "hidden";
    setTimeout(() => d.querySelector("[data-cart-close]:not([data-scrim])")?.focus({ preventScroll: true }), 300);
    return true;
  }
  function closeDrawer() {
    document.getElementById("cart-drawer")?.classList.remove("is-open");
    document.body.style.overflow = "";
  }
  function renderDrawer(sections, open) {
    const id = drawerSectionId();
    const html = sections && id && sections[id];
    if (!html) return location.reload();
    const fresh = new DOMParser().parseFromString(html, "text/html").getElementById("cart-drawer");
    const d = document.getElementById("cart-drawer");
    if (!fresh || !d) return;
    d.innerHTML = fresh.innerHTML;
    d.dataset.count = fresh.dataset.count;
    if (open) openDrawer();
    // keep the header count in step
    document.querySelectorAll("[data-cart-count]").forEach((el) => (el.textContent = fresh.dataset.count));
  }

  document.addEventListener("click", (e) => {
    const opener = e.target.closest("[data-cart-open]");
    if (opener && openDrawer()) {
      e.preventDefault();
      return;
    }
    if (e.target.closest("[data-cart-close]")) closeDrawer();

    const step = e.target.closest("[data-cart-line]");
    if (step) {
      step.disabled = true;
      fetch(`${routes()}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ line: Number(step.dataset.cartLine), quantity: Math.max(0, Number(step.dataset.cartQty)), sections: drawerSectionId() }),
      })
        .then((r) => r.json())
        .then((cart) => renderDrawer(cart.sections, false))
        .catch(() => location.reload());
    }
  });
  addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    closeDrawer();
    closeDialog();
    closeMenu();
  });

  /* ---------- the phone menu ---------- */

  const menu = document.querySelector("[data-menu]");
  const menuOpeners = document.querySelectorAll("[data-menu-open]");
  function closeMenu() {
    if (!menu?.classList.contains("is-open")) return;
    menu.classList.remove("is-open");
    document.body.style.overflow = "";
    menuOpeners.forEach((b) => b.setAttribute("aria-expanded", "false"));
  }
  menuOpeners.forEach((b) =>
    b.addEventListener("click", () => {
      menu?.classList.add("is-open");
      document.body.style.overflow = "hidden";
      b.setAttribute("aria-expanded", "true");
      setTimeout(() => menu?.querySelector("button[data-menu-close]")?.focus({ preventScroll: true }), 300);
    }),
  );
  menu?.querySelectorAll("[data-menu-close]").forEach((el) => el.addEventListener("click", closeMenu));

  document.addEventListener("submit", (e) => {
    const form = e.target.closest("form[data-add-to-bag]");
    if (!form || !document.getElementById("cart-drawer")) return;
    e.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button) button.disabled = true;
    const body = new FormData(form);
    // "one-time purchase" sends no selling plan at all
    if (body.get("selling_plan") === "") body.delete("selling_plan");
    body.append("sections", drawerSectionId());
    fetch(`${routes()}cart/add.js`, { method: "POST", headers: { Accept: "application/json" }, body })
      .then((r) => r.json())
      .then((res) => {
        if (res.status) throw new Error(res.description || res.message);
        renderDrawer(res.sections, true);
      })
      .catch((err) => alert(err.message))
      .finally(() => button && (button.disabled = false));
  });

  /* ---------- the 10% popup: once per visitor, after a delay or a third of the way down ---------- */

  const popup = document.querySelector("[data-popup]");
  const SEEN = "elixir-first-order-seen";
  function closeDialog() {
    popup?.classList.remove("is-open");
  }
  if (popup) {
    popup.querySelectorAll("[data-dialog-close]").forEach((b) => b.addEventListener("click", closeDialog));
    popup.querySelector("form")?.addEventListener("submit", () => setTimeout(closeDialog, 50));
    let seen = false;
    try {
      seen = localStorage.getItem(SEEN) === "1";
    } catch {}
    // a customer who has just signed up comes back with ?customer_posted=true
    if (location.search.includes("customer_posted=true")) seen = true;
    if (!seen && !popup.hasAttribute("data-popup-preview")) {
      let done = false;
      const show = () => {
        if (done) return;
        done = true;
        try {
          localStorage.setItem(SEEN, "1");
        } catch {}
        popup.classList.add("is-open");
        setTimeout(() => popup.querySelector("[data-popup-input]")?.focus({ preventScroll: true }), 500);
        removeEventListener("scroll", third);
      };
      // phones wait longer and further down the page, and get a slim sheet from the bottom
      const phone = matchMedia("(max-width: 767px)").matches;
      const reach = phone ? 0.5 : 0.33;
      const delay = phone ? Number(popup.dataset.popupMobileDelay) || 20 : Number(popup.dataset.popupDelay) || 8;
      const third = () => scrollProgress() > reach && show();
      setTimeout(show, delay * 1000);
      addEventListener("scroll", third, { passive: true });
    }
    // in the theme editor, show it while its section is selected
    document.addEventListener("shopify:section:select", (e) => e.target.contains(popup) && popup.classList.add("is-open"));
    document.addEventListener("shopify:section:deselect", (e) => e.target.contains(popup) && closeDialog());
  }
})();
